import { useCallback, useMemo, useState } from 'react';

import { createPortal } from 'react-dom';

import { Alert } from '@mui/material';
import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { ApiError } from '@/store/api/types';

import { HttpStatus } from '@/types/httpStatus';

import { SnackbarContext, SnackbarMessage } from './useSnackbar';

import { alertSx } from './SnackbarContext.styles';

// The key remounts MUI's Snackbar, which restarts its auto-hide timer. A counter rather than
// Date.now(): two messages added in the same millisecond would otherwise share a key, and the
// second would take over the first's remaining time.
let nextSnackbarKey = 0;

type SnackbarMessageWithKey = SnackbarMessage & {
  key: number;
};

type SnackbarCloseHandler = (
  event?: React.SyntheticEvent | Event,
  reason?: SnackbarCloseReason
) => void;

type ActiveSnackbarProps = {
  snackbar?: SnackbarMessageWithKey;
  open: boolean;
  onClose: SnackbarCloseHandler;
};

const ActiveSnackbar = ({ snackbar, open, onClose }: ActiveSnackbarProps) => {
  const {
    message = '',
    severity,
    autoHideDuration = 6000,
    key,
  } = snackbar ?? {};

  if (severity) {
    return (
      <Snackbar
        key={key}
        open={open}
        {...(autoHideDuration ? { autoHideDuration } : {})}
        onClose={onClose}
      >
        <Alert
          onClose={onClose}
          severity={severity}
          variant="filled"
          sx={alertSx}
        >
          {message}
        </Alert>
      </Snackbar>
    );
  } else {
    return (
      <Snackbar
        key={key}
        open={open}
        {...(autoHideDuration ? { autoHideDuration } : {})}
        onClose={onClose}
        message={message}
      />
    );
  }
};

export const SnackbarProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [snackbars, setSnackbars] = useState<SnackbarMessageWithKey[]>([]);
  const [open, setOpen] = useState(false);
  const [activeSnackbar, setActiveSnackbar] =
    useState<SnackbarMessageWithKey>();

  // Drain the queue during render: the newest waiting snackbar replaces whatever is showing. The
  // queue is cleared in the same step, so a burst of adds costs one extra render rather than two per
  // snackbar — React gives up after 25 render-phase updates, and this provider sits above the whole
  // app. Older waiting snackbars were only ever on screen for a single frame before the next
  // replaced them.
  if (snackbars.length) {
    setActiveSnackbar({ ...snackbars[snackbars.length - 1] });
    setSnackbars([]);
    setOpen(true);
  }

  const addSnackbar = useCallback((snackbar: SnackbarMessage) => {
    setSnackbars(snackbars => [
      ...snackbars,
      { ...snackbar, key: ++nextSnackbarKey },
    ]);
  }, []);

  const addErrorSnackbar = useCallback(
    (error: FetchBaseQueryError | SerializedError) => {
      let message = 'Unknown Error';
      let severity = 'error' as SnackbarMessage['severity'];

      if ('status' in error) {
        const status = typeof error.status === 'number' ? error.status : 0;
        const data = error.data as ApiError | undefined;

        if (status === HttpStatus.CONFLICT) {
          severity = 'warning';
        }

        switch (status) {
          case HttpStatus.UNAUTHORIZED:
            message = 'Authentication error';
            break;
          case HttpStatus.FORBIDDEN:
            message = 'Authorization error';
            break;
          case HttpStatus.INTERNAL_SERVER_ERROR:
            message = 'Internal Server Error';
            break;
          default:
            if (data?.message) {
              message = data.message;
            } else if ('error' in error && typeof error.error === 'string') {
              message = error.error;
            } else if (error.data) {
              message = JSON.stringify(error.data);
            }
        }
      } else if ('message' in error) {
        message = error.message || 'Unknown Error';
      }

      addSnackbar({
        message,
        severity,
      });
    },
    [addSnackbar]
  );

  const handleClose: SnackbarCloseHandler = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
    setActiveSnackbar(undefined);
  };

  const contextValue = useMemo(
    () => ({ addSnackbar, addErrorSnackbar }),
    [addSnackbar, addErrorSnackbar]
  );

  return (
    <SnackbarContext value={contextValue}>
      {children}
      {createPortal(
        <ActiveSnackbar
          snackbar={activeSnackbar}
          open={open}
          onClose={handleClose}
        />,
        document.body
      )}
    </SnackbarContext>
  );
};
